import { Tooltip } from "antd";
import { Procedure } from "../../../../types/procedures";
import { procedurePinRank } from "./specialBadge";
import { uploadedImageThumbPath } from "../../../../utils/uploadedImage";
import { useQuote } from "../../QuoteProvider";
import { useGetProceduresByType } from "../../hooks/useGetProceduresByType";
import "./quote-procedures-list.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import ProcedureElement from "./ProcedureElement";
// @ts-ignore - useTranslation with namespace parameter
import { useTranslation } from "react-i18next";

type QuoteProceduresListParams = {
  procedureType: string;
  setStoryProcedure?: (procedure: Procedure) => void;
};

const QuoteProceduresListAll = ({
  procedureType,
  setStoryProcedure,
}: QuoteProceduresListParams) => {
  const proceduresByType = useGetProceduresByType({ procedureType });
  const {
    selectedProcedures,
    addSelectedProcedure,
    removeSelectedProcedure,
    gender,
  } = useQuote();
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");

  // Sort procedures: specials/featured pinned first, then male-only procedures
  // at the bottom. A special/featured pin wins even over the male-only-last
  // rule — the headline procedures are what we want patients to see first.
  const sortedProcedures = [...proceduresByType].sort((a: any, b: any) => {
    const aRank = procedurePinRank(a);
    const bRank = procedurePinRank(b);
    if (aRank !== bRank) return aRank - bRank;

    const aIsMaleOnly = a.maleImage && !a.image;
    const bIsMaleOnly = b.maleImage && !b.image;

    // If one is male-only and the other isn't, male-only comes last
    if (aIsMaleOnly && !bIsMaleOnly) return 1;
    if (!aIsMaleOnly && bIsMaleOnly) return -1;
    return 0;
  });

  // Same guard as QuoteProceduresList: a bare empty grid reads as broken.
  // Reachable on "Show All" only if the gender filter empties the catalog.
  if (sortedProcedures.length === 0) {
    return (
      <div className="procedures-list-empty">
        {t("procedures.noProceduresInType", {
          defaultValue:
            "No procedures are available in this category yet. Please pick another category.",
        })}
      </div>
    );
  }

  return (
    <div className="procedures-list">
      {sortedProcedures.map((procedure: Procedure) => {
        const procedureImage =
          gender === "MALE"
            ? procedure.maleImage
            : procedure.image
              ? procedure.image
              : procedure.maleImage;

        // 240px covers 2× retina at the largest mobile display width
        // (~114px wide; Lighthouse Moto G profile is the scoring target).
        // Started at 400 but Lighthouse flagged that as ~2× too big.
        const imageUrl = procedureImage
          ? uploadedImageThumbPath(procedureImage, 240)
          : undefined;

        const isSelected = selectedProcedures.includes(procedure);

        // isDisabled - look in all the selectedProcedures for incompatibleProcedures and if found, disable this procedure
        const isDisabled = selectedProcedures.some((selectedProcedure) => {
          return (
            selectedProcedure.incompatibleProcedures?.some(
              (incompatibleProcedure) =>
                incompatibleProcedure.id === procedure.id,
            ) ||
            procedure.incompatibleProcedures?.some((incompatibleProcedure) =>
              selectedProcedure.id === incompatibleProcedure.id ? true : false,
            )
          );
        });

        const procedureElement = (
          <ProcedureElement
            procedure={procedure}
            isSelected={isSelected}
            isDisabled={isDisabled}
            removeSelectedProcedure={removeSelectedProcedure}
            addSelectedProcedure={addSelectedProcedure}
            imageUrl={imageUrl}
            setStoryProcedure={setStoryProcedure}
            selectedCount={selectedProcedures.length}
          />
        );

        return <div key={procedure.id}>{procedureElement}</div>;
      })}
    </div>
  );
};

export default QuoteProceduresListAll;
